import { gql } from "@apollo/client";

// kan-101. Every operation here is Executive-only on the server; the client
// role gate is convenience only.
//
// TempBillingReconciliationInvoice has no `id`, so Apollo cannot normalize it.
// Every consumer uses fetchPolicy: 'network-only' plus an explicit refetch after
// a mutation — never cache surgery.
//
// kan-112. An invoice is SETTLED under a payment when it is Cleared, Accepted
// (an explained reconciling item — chargeback, People 2.0 fee, credit memo,
// rounding) or in Suspense (an unexplained difference parked for later
// investigation). All three satisfy the fully-reconciled gate and appear on the
// paid-invoices sheet; `cleared*` fields stay cleared-only, `settled*` are the
// sum of the three.

/** GraphQL enum keys, exactly as they arrive over the wire. */
export type TempBillingInvoiceStatus = 'OPEN' | 'CLEARED' | 'ACCEPTED' | 'SUSPENSE';
export type TempBillingAcceptedReason =
  | 'CHARGEBACK'
  | 'FEE'
  | 'CREDIT_MEMO'
  | 'ROUNDING'
  | 'OTHER';
/** The two statuses flagTempBillingInvoices can set; Cleared has its own mutation. */
export type TempBillingFlagStatus = 'ACCEPTED' | 'SUSPENSE';

export const TEMP_BILLING_STATUS_LABEL: Record<TempBillingInvoiceStatus, string> = {
  OPEN: 'Open',
  CLEARED: 'Cleared',
  ACCEPTED: 'Accepted',
  SUSPENSE: 'Suspense',
};

/** Keyed on the GraphQL enum KEY, never the stored lowercase value. */
export const TEMP_BILLING_REASON_LABEL: Record<TempBillingAcceptedReason, string> = {
  CHARGEBACK: 'Chargeback',
  FEE: 'Fee',
  CREDIT_MEMO: 'Credit memo',
  ROUNDING: 'Rounding',
  OTHER: 'Other',
};

/** Label for a reason that may be null or (defensively) an unknown key. */
export const reasonLabel = (reason?: string | null): string =>
  reason ? TEMP_BILLING_REASON_LABEL[reason as TempBillingAcceptedReason] || reason : '';

/** MetaDataRecruiterProfitSummary, seen as a payment. `id` is the paymentMetaId. */
export interface ITempBillingPayment {
  id: string;
  fileName?: string | null;
  period?: string | null;
  monthYr?: string | null;
  uploadedByName?: string | null;
  createdAt: string;
  paymentReceivedDate?: string | null;
  paymentReferenceId?: string | null;
  activityReportMonth?: string | null;
  stamped: boolean;
  invoiceCount: number;
  /** Cleared only. */
  clearedCount: number;
  /** cleared + accepted + suspense — what the fully-reconciled gate counts. */
  settledCount: number;
  acceptedCount: number;
  suspenseCount: number;
  /** UNRESOLVED orphans: an accepted or suspense orphan no longer counts. */
  orphanCount: number;
  unkeyedRowCount: number;
  profitSummaryTotal: number;
  /** Cleared only. */
  clearedProfitTotal: number;
  settledProfitTotal: number;
  /** Gross profit on the suspense invoices. */
  suspenseProfitTotal: number;
  /** The suspense balance: net unexplained difference (profit − weekly) parked. */
  suspenseDifferenceTotal: number;
  fullyReconciled: boolean;
  activityReportAssignable: boolean;
  hasReconciliationRows: boolean;
}

/**
 * Aggregates over the WHOLE filtered payment set, not the current page — the
 * summary strip would misreport the moment the executive paginates otherwise.
 * "Open Payments" and "across N files" are both the payload's `count`; there is
 * deliberately no duplicate field for them here.
 */
export interface ITempBillingPaymentTotals {
  stampedCount: number;
  /** Profit-summary total less everything SETTLED (not just cleared). */
  unreconciledAmount: number;
  clearedInvoices: number;
  settledInvoices: number;
  acceptedInvoices: number;
  suspenseInvoices: number;
  /** Profit-summary amount currently parked in Suspense. */
  suspenseAmount: number;
  /** Net suspense balance across the filtered payments. */
  suspenseDifference: number;
  filesWithSuspense: number;
  totalInvoices: number;
  orphanCount: number;
  filesWithOrphans: number;
}

// ---- "Owed to RealREPP" (recruiter-profit recalculation) -------------------
//
// We recompute what People 2.0 OWES us on each weekly-activity line from the
// line's own amounts and the contract rates in force for its week ending:
//
//   owed = invoice amount − gross wage − People 2.0 fee% × invoice amount
//          − employee burden% × gross wage            (rounded to cents per line)
//
// and compare it with the recruiter profit People 2.0 actually paid. It is an
// INDICATOR only: it never blocks clearing, accepting or suspense.

/** Per-line outcome of the recalculation (GraphQL enum keys). */
export type TempBillingOwedStatus = 'CALCULATED' | 'EXCLUDED' | 'NOT_CALCULATED' | 'STATE_UNKNOWN';
/** Per-invoice roll-up of the line outcomes. */
export type TempBillingCalcStatus = 'CALCULATED' | 'PARTIAL' | 'NOT_CALCULATED' | 'STATE_UNKNOWN';
/** Which employee-burden rate applied: the temp lives in California or not. */
export type TempBillingBurdenKind = 'CA' | 'NON_CA';

export const TEMP_BILLING_OWED_STATUS_LABEL: Record<TempBillingOwedStatus, string> = {
  CALCULATED: 'Calculated',
  // Expense reimbursement: billed at cost, so there is no recruiter profit in it.
  EXCLUDED: 'Expense — excluded',
  NOT_CALCULATED: 'Not calculated',
  STATE_UNKNOWN: 'State unknown',
};

export const TEMP_BILLING_CALC_STATUS_LABEL: Record<TempBillingCalcStatus, string> = {
  CALCULATED: 'Calculated',
  PARTIAL: 'Partial',
  NOT_CALCULATED: 'Not calculated',
  STATE_UNKNOWN: 'State unknown',
};

export const TEMP_BILLING_BURDEN_KIND_LABEL: Record<TempBillingBurdenKind, string> = {
  CA: 'CA',
  NON_CA: 'non-CA',
};

/**
 * The recalculation of one weekly line, every input and intermediate figure
 * included so the screen can show the working, not just the answer. Dollar
 * figures are rounded to cents; percentages are plain numbers (5 means 5%).
 */
export interface ITempBillingOwedLine {
  status: TempBillingOwedStatus;
  /** Why it was not calculated, e.g. 'Before 1/1/2026', 'No rate period'. */
  reason?: string | null;
  /** 'YYYY-MM-DD' — the rate period that applied to the week ending. */
  rateEffectiveFrom?: string | null;
  feePct?: number | null;
  burdenPct?: number | null;
  burdenKind?: TempBillingBurdenKind | null;
  invoiceAmount?: number | null;
  grossWage?: number | null;
  /** 'file' = the file's gross-wage amount; 'units_x_pay' = units × pay rate (holiday lines). */
  grossWageSource?: string | null;
  fee?: number | null;
  burden?: number | null;
  /** Null unless CALCULATED; an EXCLUDED expense line is 0. */
  owed?: number | null;
  /** Both candidates when the temp's state is unknown. */
  owedIfCa?: number | null;
  owedIfNonCa?: number | null;
  /** units × rates agree with the file's amounts (within 2¢); null if an input is missing. */
  unitsAgree?: boolean | null;
  /** Full precision (6 dp), not cents — see the calculation panel. */
  profitPerHour?: number | null;
  feePerHour?: number | null;
  burdenPerHour?: number | null;
  /** profitPerHour × units, rounded once; equals `owed` when units agree with the amounts. */
  owedFromHours?: number | null;
  /** invoiceAmount ÷ billRate — the hours the billed amount implies. */
  impliedHours?: number | null;
}

/** One weekly-activity detail row behind an invoice number. */
export interface ITempBillingReconciliationLine {
  id: string;
  companyName?: string | null;
  /** RAW People 2.0 split2 string; `placementNumber` is the parsed form. */
  placementId?: string | null;
  placementNumber?: number | null;
  candidateName?: string | null;
  invoiceDate?: string | null;
  invoiceNumber?: string | null;
  periodEndDate?: string | null;
  payType?: string | null;
  units?: string | null;
  invoiceAmountNum: number;
  paidRecruiterProfitNum: number;
  metaDataId?: string | null;
  /** The file's placement id is blank or not a clean number (People 2.0 omission). */
  placementIdMissing: boolean;
  /** Our-side placement id set by the executive; the imported row is never edited. */
  placementOverrideNumber?: number | null;
  placementOverrideByName?: string | null;
  placementOverrideAt?: string | null;
  /** The override when set, else the file's number. */
  effectivePlacementNumber?: number | null;
  placementFound: boolean;
  /** Resolved state: the temp's home state, falling back to the job's. */
  stateCode?: string | null;
  isCalifornia?: boolean | null;
  grossWageNum: number;
  unitsNum?: number | null;
  payRateNum?: number | null;
  billRateNum?: number | null;
  owed: ITempBillingOwedLine;
}

export interface ITempBillingReconciliationInvoice {
  /** Display value (raw, trimmed). `invoiceKey` is what mutations take. */
  invoiceNumber: string;
  invoiceKey: string;
  profitSummaryTotal: number;
  weeklyActivityTotal: number;
  invoiceAmountTotal: number;
  difference: number;
  matched: boolean;
  orphan: boolean;
  status: TempBillingInvoiceStatus;
  /** status === CLEARED. */
  cleared: boolean;
  /** status !== OPEN. */
  settled: boolean;
  /** Clearing is from OPEN only — a flagged invoice is reopened first. */
  clearable: boolean;
  /** Can be put in (or moved between) Accepted / Suspense. */
  flaggable: boolean;
  /** Accepted is only for an orphan or a mismatch; a matched invoice is Cleared. */
  acceptable: boolean;
  flagReason?: TempBillingAcceptedReason | null;
  flagNote?: string | null;
  blockedByPaymentMetaId?: string | null;
  blockedByPaymentReferenceId?: string | null;
  profitSummaryRowCount: number;
  weeklyActivityRowCount: number;
  companyName?: string | null;
  employeeName?: string | null;
  invoiceDate?: string | null;
  periodEndDate?: string | null;
  placementId?: string | null;
  placementNumber?: number | null;
  /** The SETTLED stamp — cleared, accepted or put in suspense. */
  clearedAt?: string | null;
  clearedByName?: string | null;
  unclearedAt?: string | null;
  unclearedByName?: string | null;
  /**
   * Σ owed over the calculated and excluded lines. On a settled invoice this is
   * the figure frozen at settle time, so later rate changes do not rewrite it.
   */
  owedTotal?: number | null;
  /** owedTotal − weeklyActivityTotal. */
  calcDifference?: number | null;
  /** |calcDifference| ≤ $0.05; only set when calcStatus is CALCULATED. */
  calcMatched?: boolean | null;
  calcStatus: TempBillingCalcStatus;
  /** When a line's state is unknown: the total if the temp lives in CA, and if not. */
  owedTotalIfCa?: number | null;
  owedTotalIfNonCa?: number | null;
  linesMissingPlacement: number;
  lines: Array<ITempBillingReconciliationLine>;
  profitLines: Array<ITempBillingProfitLine>;
}

/** A Recruiter Profit Summary row — the payment side of an invoice pair. */
export interface ITempBillingProfitLine {
  id: string;
  companyName?: string | null;
  placementId?: string | null;
  placementNumber?: number | null;
  employeeName?: string | null;
  invoiceDate?: string | null;
  invoiceNumber?: string | null;
  periodEndDate?: string | null;
  units?: string | null;
  invoiceAmountNum: number;
  grossRecruiterProfitNum: number;
}

/** Built from the settling-time snapshot, never from live rows. */
export interface ITempBillingPaidInvoiceRow {
  invoiceNumber: string;
  /** Which payment paid it — the activity-report sheet spans several. */
  paymentReferenceId?: string | null;
  companyName?: string | null;
  candidateName?: string | null;
  placementId?: string | null;
  invoiceDate?: string | null;
  periodEndDate?: string | null;
  invoiceAmount: number;
  weeklyActivityTotal: number;
  profitSummaryTotal: number;
  matched: boolean;
  status: TempBillingInvoiceStatus;
  flagReason?: TempBillingAcceptedReason | null;
  flagNote?: string | null;
  /** Frozen at settle time; null when the invoice was not fully calculable then. */
  owedTotal?: number | null;
  calcMatched?: boolean | null;
  /** The SETTLED stamp — labelled "Settled On/By" on the sheet. */
  clearedAt: string;
  clearedByName?: string | null;
}

export interface ITempBillingReconcileSkip {
  invoiceNumber: string;
  reason: string;
}

export interface ITempBillingReconcileResult {
  affectedCount: number;
  skipped: Array<ITempBillingReconcileSkip>;
}

/** Skip reasons come back as the GraphQL enum key; these are what we show. */
export const TEMP_BILLING_SKIP_REASON_TEXT: Record<string, string> = {
  ORPHAN: 'no matching weekly activity',
  ALREADY_CLEARED: 'already cleared',
  NOT_CLEARED: 'not cleared or flagged',
  // Returned for ANY settled state on the other payment, not only cleared —
  // say so, or the accountant goes looking for a clear that never happened.
  CLEARED_ON_ANOTHER_PAYMENT: 'already settled (cleared, accepted or in suspense) on another payment',
  FLAGGED: 'reopen the flagged invoice before clearing it',
  MATCHED: 'matched invoices should be cleared, not accepted',
  NOT_ON_PAYMENT: 'not on this payment',
  PAYMENT_NOT_STAMPED: 'payment has not been stamped yet',
  ACTIVITY_REPORT_ASSIGNED: 'activity report is assigned — remove it first',
};

export const TEMP_BILLING_PAYMENTS = gql`
query($payload: PageCountInput!, $filters: TempBillingPaymentsFilter) {
  tempBillingPayments(payload: $payload, filters: $filters) {
    payments {
      id
      fileName
      period
      monthYr
      uploadedByName
      createdAt
      paymentReceivedDate
      paymentReferenceId
      activityReportMonth
      stamped
      invoiceCount
      clearedCount
      settledCount
      acceptedCount
      suspenseCount
      orphanCount
      unkeyedRowCount
      profitSummaryTotal
      clearedProfitTotal
      settledProfitTotal
      suspenseProfitTotal
      suspenseDifferenceTotal
      fullyReconciled
      activityReportAssignable
      hasReconciliationRows
    }
    count
    totals {
      stampedCount
      unreconciledAmount
      clearedInvoices
      settledInvoices
      acceptedInvoices
      suspenseInvoices
      suspenseAmount
      suspenseDifference
      filesWithSuspense
      totalInvoices
      orphanCount
      filesWithOrphans
    }
    availableActivityReports
    message
    status
  }
}`

export const TEMP_BILLING_PAYMENT_RECONCILIATION = gql`
query($paymentMetaId: String!, $onlyUncleared: Boolean) {
  tempBillingPaymentReconciliation(paymentMetaId: $paymentMetaId, onlyUncleared: $onlyUncleared) {
    payment {
      id
      fileName
      period
      monthYr
      uploadedByName
      createdAt
      paymentReceivedDate
      paymentReferenceId
      activityReportMonth
      stamped
      invoiceCount
      clearedCount
      settledCount
      acceptedCount
      suspenseCount
      orphanCount
      unkeyedRowCount
      profitSummaryTotal
      clearedProfitTotal
      settledProfitTotal
      suspenseProfitTotal
      suspenseDifferenceTotal
      fullyReconciled
      activityReportAssignable
      hasReconciliationRows
    }
    invoices {
      invoiceNumber
      invoiceKey
      profitSummaryTotal
      weeklyActivityTotal
      invoiceAmountTotal
      difference
      matched
      orphan
      status
      cleared
      settled
      clearable
      flaggable
      acceptable
      flagReason
      flagNote
      blockedByPaymentMetaId
      blockedByPaymentReferenceId
      profitSummaryRowCount
      weeklyActivityRowCount
      companyName
      employeeName
      invoiceDate
      periodEndDate
      placementId
      placementNumber
      clearedAt
      clearedByName
      unclearedAt
      unclearedByName
      owedTotal
      calcDifference
      calcMatched
      calcStatus
      owedTotalIfCa
      owedTotalIfNonCa
      linesMissingPlacement
      lines {
        id
        companyName
        placementId
        placementNumber
        candidateName
        invoiceDate
        invoiceNumber
        periodEndDate
        payType
        units
        invoiceAmountNum
        paidRecruiterProfitNum
        metaDataId
        placementIdMissing
        placementOverrideNumber
        placementOverrideByName
        placementOverrideAt
        effectivePlacementNumber
        placementFound
        stateCode
        isCalifornia
        grossWageNum
        unitsNum
        payRateNum
        billRateNum
        owed {
          status
          reason
          rateEffectiveFrom
          feePct
          burdenPct
          burdenKind
          invoiceAmount
          grossWage
          grossWageSource
          fee
          burden
          owed
          owedIfCa
          owedIfNonCa
          unitsAgree
          profitPerHour
          feePerHour
          burdenPerHour
          owedFromHours
          impliedHours
        }
      }
      profitLines {
        id
        companyName
        placementId
        placementNumber
        employeeName
        invoiceDate
        invoiceNumber
        periodEndDate
        units
        invoiceAmountNum
        grossRecruiterProfitNum
      }
    }
    message
    status
  }
}`

/**
 * Every settled invoice across EVERY payment carrying one activity-report tag.
 * Same rows and same settling-time snapshots as TEMP_BILLING_PAID_INVOICES —
 * only the scope is wider, so the two sheets cannot disagree about an invoice.
 */
export const TEMP_BILLING_ACTIVITY_REPORT_INVOICES = gql`
query($activityReportMonth: String!) {
  tempBillingActivityReportInvoices(activityReportMonth: $activityReportMonth) {
    activityReportMonth
    paymentReferenceIds
    paymentCount
    rows {
      invoiceNumber
      paymentReferenceId
      companyName
      candidateName
      placementId
      invoiceDate
      periodEndDate
      invoiceAmount
      weeklyActivityTotal
      profitSummaryTotal
      matched
      status
      flagReason
      flagNote
      owedTotal
      calcMatched
      clearedAt
      clearedByName
    }
    message
    status
  }
}`

export const TEMP_BILLING_PAID_INVOICES = gql`
query($paymentMetaId: String!) {
  tempBillingPaidInvoices(paymentMetaId: $paymentMetaId) {
    payment {
      id
      fileName
      paymentReferenceId
      paymentReceivedDate
      activityReportMonth
    }
    rows {
      invoiceNumber
      paymentReferenceId
      companyName
      candidateName
      placementId
      invoiceDate
      periodEndDate
      invoiceAmount
      weeklyActivityTotal
      profitSummaryTotal
      matched
      status
      flagReason
      flagNote
      owedTotal
      calcMatched
      clearedAt
      clearedByName
    }
    message
    status
  }
}`

export const STAMP_TEMP_BILLING_PAYMENT = gql`
mutation($input: StampTempBillingPaymentInput!) {
  stampTempBillingPayment(input: $input) {
    payment {
      id
      paymentReceivedDate
      paymentReferenceId
      stamped
    }
    message
    status
  }
}`

export const CLEAR_TEMP_BILLING_INVOICES = gql`
mutation($input: TempBillingReconcileInput!) {
  clearTempBillingInvoices(input: $input) {
    affectedCount
    skipped {
      invoiceNumber
      reason
    }
    message
    status
  }
}`

export const UNCLEAR_TEMP_BILLING_INVOICES = gql`
mutation($input: TempBillingReconcileInput!) {
  unclearTempBillingInvoices(input: $input) {
    affectedCount
    skipped {
      invoiceNumber
      reason
    }
    message
    status
  }
}`

/**
 * kan-112. Puts invoices in Accepted or Suspense (or moves them between the two,
 * or edits the reason/note). `reason` only with ACCEPTED; `note` is required
 * for SUSPENSE. Same result shape as clear/unclear, so the same skipped-aware
 * toast applies.
 */
export const FLAG_TEMP_BILLING_INVOICES = gql`
mutation($input: TempBillingFlagInput!) {
  flagTempBillingInvoices(input: $input) {
    affectedCount
    skipped {
      invoiceNumber
      reason
    }
    message
    status
  }
}`

/** A People 2.0 cost-rate period. Append-only: history under settled invoices never changes. */
export interface ITempBillingCostRate {
  id: string;
  /** 'YYYY-MM-DD' — applies to weeks ENDING on or after this date. */
  effectiveFrom: string;
  people20FeePct: number;
  burdenCaPct: number;
  burdenNonCaPct: number;
  note?: string | null;
  createdAt: string;
  /** Null for the periods seeded by the migration. */
  createdBy?: { id: string; firstName?: string | null; lastName?: string | null } | null;
}

/** Newest first. `cutoff` is the first week ending that is calculated at all. */
export const TEMP_BILLING_COST_RATES = gql`
query {
  tempBillingCostRates {
    rates {
      id
      effectiveFrom
      people20FeePct
      burdenCaPct
      burdenNonCaPct
      note
      createdAt
      createdBy {
        id
        firstName
        lastName
      }
    }
    cutoff
  }
}`

export const ADD_TEMP_BILLING_COST_RATE = gql`
mutation($input: AddTempBillingCostRateInput!) {
  addTempBillingCostRate(input: $input) {
    rate {
      id
      effectiveFrom
      people20FeePct
      burdenCaPct
      burdenNonCaPct
      note
      createdAt
    }
  }
}`

/**
 * Sets (or, with placementNumber null, clears) our-side placement id for a
 * weekly line People 2.0 sent without one. Stored in its own table with who and
 * when; the imported row is never edited.
 */
export const SET_TEMP_BILLING_WEEKLY_LINE_PLACEMENT = gql`
mutation($input: SetTempBillingWeeklyLinePlacementInput!) {
  setTempBillingWeeklyLinePlacement(input: $input) {
    weeklyLineId
    placementNumber
    placementFound
    stateCode
    isCalifornia
    setByName
    setAt
  }
}`

export const ASSIGN_TEMP_BILLING_ACTIVITY_REPORT = gql`
mutation($input: AssignTempBillingActivityReportInput!) {
  assignTempBillingActivityReport(input: $input) {
    payment {
      id
      activityReportMonth
      activityReportAssignable
    }
    message
    status
  }
}`
